// Pure helpers that turn a resolved product offer + region into the props for
// next-seo's <ProductJsonLd>. Server-safe (no window / no Overmind import) so it
// runs identically in SSR and on the client and can be unit-tested in isolation.
//
// Emitting Product structured data makes the product/commerce pages eligible for
// Google rich results + the free Shopping listings (task #24). The JSON-LD is
// rendered into the SSR HTML, per region, so /us shows USD, /eu EUR, /au AUD.

import { regionDisplay } from './regions'
import { getShopItemPrice, isBundle } from './shopItemsAndCart'
import { isOutOfStock, isPreOrder, isUnavailableInRegion } from './shopItems'

export const AVAILABILITY = {
  IN_STOCK: 'https://schema.org/InStock',
  OUT_OF_STOCK: 'https://schema.org/OutOfStock',
  PRE_ORDER: 'https://schema.org/PreOrder',
}

// schema.org availability for a single offer item, derived from the same stock
// helpers the rest of the shop uses (pre-order wins over out-of-stock).
export function availabilityForItem(item) {
  if (!item) {
    return AVAILABILITY.OUT_OF_STOCK
  }
  if (isPreOrder(item)) {
    return AVAILABILITY.PRE_ORDER
  }
  return isOutOfStock(item) ? AVAILABILITY.OUT_OF_STOCK : AVAILABILITY.IN_STOCK
}

// Decimal price string ("199.00") from a { priceCents } price object.
function decimalPrice(priceObj) {
  if (!priceObj || typeof priceObj.priceCents !== 'number') {
    return null
  }
  return (priceObj.priceCents / 100).toFixed(2)
}

// ISO date ~one year out — a conservative priceValidUntil so Google does not
// treat the offer as expired (it is optional but recommended for Merchant).
export function defaultPriceValidUntil(from = new Date()) {
  const d = new Date(from)
  d.setFullYear(d.getFullYear() + 1)
  return d.toISOString().slice(0, 10)
}

// Best image URLs for an item: gallery first (full set image when it is a set),
// then the single imageUrl. De-duped, absolute URLs only.
export function imagesForItem(item) {
  if (!item) {
    return []
  }
  const urls = []
  const push = (u) => {
    // Accept a plain URL string or a ResolvedImage descriptor ({ src }). Catalog
    // images are normalized to descriptors server-side (task #67), so read .src.
    const url = typeof u === 'string' ? u : u && typeof u === 'object' ? u.src : null
    if (typeof url === 'string' && url && !urls.includes(url)) {
      urls.push(url)
    }
  }
  ;(item.galleryImages || []).forEach((img) => push(img))
  push(item.imageUrl)
  return urls.filter((u) => /^https?:\/\//.test(u))
}

// SKU/MPN source: there is no real SKU on the offer DTO, so we use the
// productSetId (the slug-stable product identifier, also used in /product/<id>
// URLs) as the SKU and the backend item `type` enum as the MPN.
export function skuForItem(item) {
  return (item && (item.productSetId || item.type || item.id)) || undefined
}

export function mpnForItem(item) {
  return (item && (item.type || item.id)) || undefined
}

// Build one next-seo Offer from a single priced item.
function offerForItem(item, { currency, url, priceValidUntil }) {
  const priceObj = getShopItemPrice(item)
  const price = decimalPrice(priceObj)
  if (!price) {
    return null
  }
  const offer = {
    price,
    priceCurrency: currency || (priceObj && priceObj.currency) || 'USD',
    availability: availabilityForItem(item),
    seller: { name: 'Virtual Racing School' },
  }
  if (url) {
    offer.url = url
  }
  if (priceValidUntil) {
    offer.priceValidUntil = priceValidUntil
  }
  return offer
}

/**
 * Build the full <ProductJsonLd> prop bag from a resolved product.
 *
 * @param {object}   args
 * @param {object}   args.primary    the primary offer item (DTO from shopItems)
 * @param {object[]} [args.variants] all priced variant items (incl. primary) for
 *                                   AggregateOffer; omit/empty -> single Offer
 * @param {string}   [args.region]   indexed region ('us'|'eu'|'au') -> currency
 * @param {string}   [args.url]      canonical region URL of the page
 * @param {string}   [args.name]     display name override (Sanity section title)
 * @param {string}   [args.description]
 * @returns {object|null}  props for <ProductJsonLd>, or null if not emittable
 *                         (no resolved item / no real price -> emit nothing).
 */
export function buildProductJsonLdProps({
  primary,
  variants,
  region,
  url,
  name,
  description,
}) {
  if (!primary) {
    return null
  }

  // Currency is DERIVED from the page region so /us=USD, /eu=EUR, /au=AUD even if
  // a stray item carried a different currency. Falls back to the item's own
  // price currency when region is unknown.
  const currency =
    (region && regionDisplay(region).currency) ||
    (getShopItemPrice(primary) && getShopItemPrice(primary).currency) ||
    'USD'

  const priceValidUntil = defaultPriceValidUntil()

  // Priced variants only (an item with no price can't carry an Offer).
  const pricedVariants = (variants || [primary]).filter(
    (i) => i && decimalPrice(getShopItemPrice(i))
  )

  // Resilience: if nothing has a real price (e.g. SSR price-bundle miss), emit
  // nothing rather than a zero-price / broken Product block.
  const primaryOffer = offerForItem(primary, { currency, url, priceValidUntil })
  if (!primaryOffer && !pricedVariants.length) {
    return null
  }

  const productName =
    name || primary.namePublicShop || primary.name || primary.type

  const props = {
    productName,
    images: imagesForItem(primary),
    brand: 'VRS',
    manufacturerName: 'Virtual Racing School',
    sku: skuForItem(primary),
    mpn: mpnForItem(primary),
  }

  const desc = description || primary.fullSetDescription || primary.description
  if (desc) {
    props.description = desc
  }

  // Multiple distinct priced variants -> AggregateOffer with low/high; a single
  // variant -> a single Offer. "Distinct" is judged by the decimal price so two
  // identically-priced colorways don't masquerade as a price range.
  const prices = pricedVariants
    .map((i) => parseFloat(decimalPrice(getShopItemPrice(i))))
    .filter((n) => !Number.isNaN(n))
  const lo = Math.min(...prices)
  const hi = Math.max(...prices)

  if (pricedVariants.length > 1 && hi > lo) {
    props.aggregateOffer = {
      priceCurrency: currency,
      lowPrice: lo.toFixed(2),
      highPrice: hi.toFixed(2),
      offerCount: String(pricedVariants.length),
      // Nested per-variant offers retain availability + canonical url +
      // priceValidUntil, which the bare AggregateOffer wrapper drops.
      offers: pricedVariants
        .map((i) => offerForItem(i, { currency, url, priceValidUntil }))
        .filter(Boolean),
    }
  } else {
    props.offers = [
      primaryOffer ||
        offerForItem(pricedVariants[0], { currency, url, priceValidUntil }),
    ]
  }

  return props
}

/**
 * Build an ItemList of Product JSON-LD for the shop/store catalog grid (audit
 * C-H2 #2). /shop and /store list the whole catalog with zero structured data;
 * this emits ONE schema.org ItemList whose elements are compact Products (name +
 * image + a single Offer in each item's own currency, so /us=USD, /eu=EUR, …).
 * Order follows the grid order of the items passed in.
 *
 * Only priced, shop-visible items become list entries; returns null when nothing
 * is emittable (never an empty/broken ItemList).
 *
 * @param {object}   args
 * @param {object[]} args.items  displayed shop items (region-resolved offers)
 * @param {string}   [args.name] optional ItemList name
 * @returns {object|null} the schema.org ItemList object, or null.
 */
export function buildShopItemListJsonLd({ items, name } = {}) {
  if (!Array.isArray(items) || !items.length) {
    return null
  }
  const elements = []
  items.forEach((item) => {
    if (!item || !item.displayOnShop) {
      return
    }
    const priceObj = getShopItemPrice(item)
    const price = decimalPrice(priceObj)
    if (!price) {
      return
    }
    const product = {
      '@type': 'Product',
      name: item.namePublicShop || item.name || item.type,
      brand: { '@type': 'Brand', name: 'VRS' },
      offers: {
        '@type': 'Offer',
        price,
        priceCurrency: (priceObj && priceObj.currency) || 'USD',
        availability: availabilityForItem(item),
        seller: { name: 'Virtual Racing School' },
      },
    }
    const images = imagesForItem(item)
    if (images.length) {
      product.image = images
    }
    const sku = skuForItem(item)
    if (sku) {
      product.sku = sku
    }
    const desc = item.fullSetDescription || item.description
    if (desc) {
      product.description = desc
    }
    elements.push({
      '@type': 'ListItem',
      position: elements.length + 1,
      item: product,
    })
  })
  if (!elements.length) {
    return null
  }
  const list = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: elements,
  }
  if (name) {
    list.name = name
  }
  return list
}

// ---------------------------------------------------------------------------
// Per-section item resolution. Each product section type stores its product
// reference differently (itemType, mainItemTypes[], or a GT2 flag), so this
// resolves { primary, variants } from the section + the SSR-hydrated shopItems.
// Mirrors how each section component already finds its item, kept in one place
// so the JSON-LD never drifts from the rendered product.
// ---------------------------------------------------------------------------

const SINGLE_PRODUCT_SECTION_TYPES = new Set([
  'productCheckoutPage',
  'specsPage',
  'gt2CockpitCheckoutPage',
  'gt2FullRigCheckoutPage',
  'upgradableWheelBaseCheckoutPage',
  'uDFP20UpgradesCheckoutPage',
])

// Sentinel itemType for the GT-2 cockpit OVERVIEW page. The cockpit is a colorway
// SET (many `isGT2Cockpit` offers), not a single `.type`, so it is resolved by the
// isGT2Cockpit flag (same as the gt2CockpitCheckoutPage case) rather than byType.
// Kept distinct from any real item `.type` so byType can never accidentally match.
export const GT2_COCKPIT_ITEM_TYPE = '__GT2_COCKPIT_SET__'

// Hardware overview / marketing pages (e.g. /hardware/pro-pedals, the formula
// wheels, the GT-2 cockpit) are built from hero templates (pedalsHero /
// liteFormulaWheelHero / productOverviewHero / productImageAndSpecs) that carry NO
// itemType field — so they never matched findProductSection and emitted no Product
// schema even though ProductJsonLdRaw is already wired (audit C-H2 #1). Their only
// in-page product signal is the "buy" CTA, whose target is either a dereferenced
// checkout route (`route->` deref'd by the page GROQ -> routeOrUrl.route.slug) OR a
// hardcoded region URL (routeOrUrl.url, e.g. '/eu/product/gt2-cockpit'). This maps
// that checkout-route slug -> the standalone product's itemType so the overview
// page emits the SAME Product/Offer (per-region currency + priceValidUntil) as its
// /product/<slug> checkout page. Extend as more hardware landing pages ship.
const CHECKOUT_ROUTE_SLUG_TO_ITEM_TYPE = {
  'product/pro-pedals': 'PEDALS_THROTTLE_AND_BRAKE',
  'product/lite-formula-wheel': 'LITE_FORMULA_STEERING_WHEEL',
  'product/r295-g2-steering-wheel': 'R295_STEERING_WHEEL_G2',
  'product/gt2-cockpit': GT2_COCKPIT_ITEM_TYPE,
}

const HARDWARE_HERO_SECTION_TYPES = new Set([
  'pedalsHero',
  'productImageAndSpecs',
  'liteFormulaWheelHero',
  'productOverviewHero',
])

// Region-prefix segments to strip when reading a checkout slug out of a hardcoded
// CTA url (e.g. '/eu/product/gt2-cockpit' -> 'product/gt2-cockpit').
const REGION_URL_PREFIX_REGEX = /^(us|eu|au|world)\//

// The checkout-route slug a single CTA targets, normalized to a bare
// 'product/<slug>' form. Resolves EITHER a dereferenced route
// (routeOrUrl.route.slug.current) OR a hardcoded url (routeOrUrl.url), stripping the
// origin, leading slash, any region prefix and any query/hash. Returns null when
// the CTA has no resolvable target.
function ctaCheckoutSlug(cta) {
  if (!cta || !cta.routeOrUrl) {
    return null
  }
  const routeSlug =
    cta.routeOrUrl.route &&
    cta.routeOrUrl.route.slug &&
    cta.routeOrUrl.route.slug.current
  if (routeSlug) {
    return routeSlug
  }
  const url = cta.routeOrUrl.url
  if (typeof url === 'string' && url) {
    let path = url.replace(/^https?:\/\/[^/]+/, '') // drop origin if absolute
    path = path.split(/[?#]/)[0] // drop query/hash
    path = path.replace(/^\//, '') // drop leading slash
    path = path.replace(REGION_URL_PREFIX_REGEX, '') // drop region prefix
    return path || null
  }
  return null
}

// The checkout-route slug a hero section's buy CTA points at, if it maps to a known
// product. Reads ctaV2 (as pedalsHero uses), cta (singular, as the wheel/cockpit
// liteFormulaWheelHero uses) and any ctasV2[].
function checkoutSlugFromHeroCta(section) {
  if (!section) {
    return null
  }
  const ctas = []
  if (section.ctaV2) ctas.push(section.ctaV2)
  if (section.cta) ctas.push(section.cta)
  if (Array.isArray(section.ctasV2)) ctas.push(...section.ctasV2)
  for (const cta of ctas) {
    const slug = ctaCheckoutSlug(cta)
    if (slug && CHECKOUT_ROUTE_SLUG_TO_ITEM_TYPE[slug]) {
      return slug
    }
  }
  return null
}

// The itemType a hardware-hero PAGE resolves to: scan the whole content for the
// first hero whose buy CTA maps to a known checkout slug. Page-level (not
// per-section) so a hero WITHOUT its own CTA (productImageAndSpecs) still resolves
// via the page's pedalsHero CTA.
export function hardwareHeroItemType(content) {
  if (!Array.isArray(content)) {
    return null
  }
  for (const s of content) {
    if (s && HARDWARE_HERO_SECTION_TYPES.has(s._type)) {
      const slug = checkoutSlugFromHeroCta(s)
      if (slug) {
        return CHECKOUT_ROUTE_SLUG_TO_ITEM_TYPE[slug]
      }
    }
  }
  return null
}

// The single product section on a page, if any. A page renders at most one real
// product, so the first matching section wins. (productOffer is a promo banner
// with no item/price and is intentionally NOT a product page.)
//
// Real checkout/product sections win. Failing that, a hardware overview page is
// matched by its hero template: the first hero section is returned tagged with the
// page-resolved itemType (`_resolvedItemType`) so resolveSectionProduct can resolve
// it against shopItems without re-scanning the whole content array.
export function findProductSection(content) {
  if (!Array.isArray(content)) {
    return null
  }
  const checkout = content.find(
    (s) => s && SINGLE_PRODUCT_SECTION_TYPES.has(s._type)
  )
  if (checkout) {
    return checkout
  }
  const itemType = hardwareHeroItemType(content)
  if (itemType) {
    const hero = content.find(
      (s) => s && HARDWARE_HERO_SECTION_TYPES.has(s._type)
    )
    if (hero) {
      return { ...hero, _resolvedItemType: itemType }
    }
  }
  return null
}

const byType = (type, shopItems) =>
  type && shopItems ? shopItems.find((i) => i.type === type) : undefined

/**
 * Resolve { primary, variants } for a product section against shopItems.
 * Returns null when the section's product cannot be resolved (renders nothing).
 */
export function resolveSectionProduct(section, shopItems) {
  if (!section || !Array.isArray(shopItems) || !shopItems.length) {
    return null
  }

  switch (section._type) {
    case 'gt2CockpitCheckoutPage': {
      // The GT-2 cockpit page renders the colorway cockpit items; offer the full
      // set of cockpits as variants (AggregateOffer across colors).
      const cockpits = shopItems.filter((i) => i.isGT2Cockpit)
      const primary =
        cockpits.find((i) => !isOutOfStock(i)) || cockpits[0]
      if (!primary) {
        return null
      }
      return { primary, variants: cockpits }
    }

    case 'gt2FullRigCheckoutPage': {
      // One product sold in seven cockpit colours, each its own bundle offer.
      // Variants are resolved by productSetId like the productCheckoutPage case,
      // but WITHOUT its `displayOnShop && !isBundle` filter: every colour here is
      // a bundle and the whole set is intentionally unlisted, so that filter would
      // drop all of them. Priced items only — an unpriced colour is one this
      // region cannot sell.
      const primary = byType(section.itemType, shopItems)
      if (!primary) {
        return null
      }
      const variants = shopItems.filter(
        (i) => i.productSetId === primary.productSetId && !isUnavailableInRegion(i)
      )
      return { primary, variants: variants.length ? variants : [primary] }
    }

    case 'upgradableWheelBaseCheckoutPage': {
      const types = Array.isArray(section.mainItemTypes)
        ? section.mainItemTypes
        : []
      const items = types.map((t) => byType(t, shopItems)).filter(Boolean)
      const primary = items.find((i) => !isOutOfStock(i)) || items[0]
      if (!primary) {
        return null
      }
      return { primary, variants: items }
    }

    case 'productCheckoutPage':
    case 'uDFP20UpgradesCheckoutPage': {
      const primary =
        byType(section.itemType, shopItems) ||
        byType(section.fallbackItemType, shopItems)
      if (!primary) {
        return null
      }
      // Variants = other visible items sharing the productSetId (colorways /
      // set members), matching how getItemOffers builds the offer list.
      const variants = primary.productSetId
        ? shopItems.filter(
            (i) =>
              i.productSetId === primary.productSetId &&
              i.displayOnShop &&
              !isBundle(i)
          )
        : [primary]
      return { primary, variants: variants.length ? variants : [primary] }
    }

    case 'specsPage': {
      const primary = byType(section.itemType, shopItems)
      if (!primary) {
        return null
      }
      return { primary, variants: [primary] }
    }

    case 'pedalsHero':
    case 'productImageAndSpecs':
    case 'liteFormulaWheelHero':
    case 'productOverviewHero': {
      // Hardware overview page (audit C-H2 #1). itemType comes from the page-level
      // hero-CTA resolution stashed on the section by findProductSection, falling
      // back to this section's own buy CTA. Variants/primary resolution mirrors the
      // productCheckoutPage case so the emitted Product matches the checkout page.
      const slug = checkoutSlugFromHeroCta(section)
      const itemType =
        section._resolvedItemType ||
        (slug && CHECKOUT_ROUTE_SLUG_TO_ITEM_TYPE[slug])
      if (!itemType) {
        return null
      }
      // GT-2 cockpit overview -> the colorway cockpit SET (same resolution as the
      // gt2CockpitCheckoutPage case), an AggregateOffer across colors. On a region
      // where the cockpit is unavailable (EU-only rig — #46/#137), the filtered
      // items carry no price, so buildProductJsonLdProps emits nothing there.
      if (itemType === GT2_COCKPIT_ITEM_TYPE) {
        const cockpits = shopItems.filter((i) => i.isGT2Cockpit)
        const primary = cockpits.find((i) => !isOutOfStock(i)) || cockpits[0]
        if (!primary) {
          return null
        }
        return { primary, variants: cockpits }
      }
      const primary = byType(itemType, shopItems)
      if (!primary) {
        return null
      }
      const variants = primary.productSetId
        ? shopItems.filter(
            (i) =>
              i.productSetId === primary.productSetId &&
              i.displayOnShop &&
              !isBundle(i)
          )
        : [primary]
      return { primary, variants: variants.length ? variants : [primary] }
    }

    default:
      return null
  }
}

/**
 * SEO region-noindex decision for a page (task #144).
 *
 * A product page whose primary item is UNAVAILABLE in the resolved region (an
 * EU-only rig viewed on /us, /au or /world — #46/#137) renders the thin "not
 * available for your location" state instead of a real buy box. Those region
 * variants are low-value/near-duplicate for search, so they should be
 * `noindex,follow` while the region where the product IS sold stays fully
 * indexable.
 *
 * This reuses the SAME primary-item resolution the rendered page and the Product
 * JSON-LD use (findProductSection + resolveSectionProduct), so the noindex
 * decision can never drift from what #137 actually shows the visitor. It gates on
 * the exact shared predicate the checkout sections gate their "unavailable" UX on
 * (isUnavailableInRegion), so metadata and UX agree by construction.
 *
 * FAIL-OPEN (SEO safety): returns false (indexable) whenever the page is not a
 * product page, the primary item cannot be resolved (e.g. a transient SSR
 * price-bundle miss leaves shopItems empty), or the item carries a real price.
 * We NEVER noindex a region where the item is actually sold — only the variants
 * that render the unavailable state.
 *
 * @param {Array<any>|null|undefined} content    the page's Sanity content array
 * @param {Array<any>|null|undefined} shopItems  the region-resolved shop offers
 * @returns {boolean} true => emit robots noindex,follow for this region variant
 */
export function isProductPageUnavailableInRegion(content, shopItems) {
  const section = findProductSection(content)
  if (!section) {
    return false
  }
  // The #144 region-noindex applies ONLY to real product CHECKOUT pages (the
  // region-prefixed /xx/product/* variants). A hardware-hero OVERVIEW page (pedals
  // / wheels / cockpit, matched via its buy CTA) is a SINGLE canonical content URL,
  // NOT a per-region variant — noindex-ing it because the product is unavailable in
  // the crawl region would deindex the whole page (e.g. the EU-only GT-2 cockpit
  // crawled from the US). Those overview pages simply emit no Product/Offer in a
  // region where the product has no price; they always stay indexable. (pedalsHero
  // already fell through here harmlessly because pedals are sold in every region;
  // this makes the intent explicit and safe for the region-restricted cockpit.)
  if (!SINGLE_PRODUCT_SECTION_TYPES.has(section._type)) {
    return false
  }
  const resolved = resolveSectionProduct(section, shopItems)
  if (!resolved || !resolved.primary) {
    return false
  }
  return isUnavailableInRegion(resolved.primary)
}
